import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatContext = createContext();

export const ChatProvider = ({ children, communicationType = 'graphql', httpOptions = {} }) => {
  const [conversations, setConversations] = useState({});
  const [communicationConfig] = useState({
    type: communicationType,
    httpOptions: httpOptions
  });
  
  const getConversation = useCallback((agentId) => {
    const conversation = conversations[agentId] || {
      messages: [],
      conversationID: "",
      agentIsTyping: false,
      showStopButton: false,
      isSendMessageClicked: false,
      hasMessages: false
    };
    
    if (!Array.isArray(conversation.messages)) {
      conversation.messages = [];
    }
    
    return conversation;
  }, [conversations]);

  const updateConversation = useCallback((agentId, updates) => {
    setConversations(prev => {
      const currentConversation = prev[agentId] || {
        messages: [],
        conversationID: "",
        agentIsTyping: false,
        showStopButton: false,
        isSendMessageClicked: false,
        hasMessages: false
      };

      const updatedConversation = {
        ...currentConversation,
        ...updates
      };

      if (!Array.isArray(updatedConversation.messages)) {
        updatedConversation.messages = [];
      }

      return {
        ...prev,
        [agentId]: updatedConversation
      };
    });
  }, []);

  const clearConversation = useCallback((agentId) => {
    setConversations(prev => {
      const newState = {
        ...prev,
        [agentId]: {
          messages: [],
          conversationID: "",
          agentIsTyping: false,
          showStopButton: false,
          isSendMessageClicked: false,
          hasMessages: false
        }
      };
      
      return newState;
    });
  }, []);

  const setMessages = useCallback((agentId, messages) => {
    const safeMessages = Array.isArray(messages) ? messages : [];
    updateConversation(agentId, { 
      messages: safeMessages,
      hasMessages: safeMessages.length > 0
    });
  }, [updateConversation]);

  const setAgentIsTyping = useCallback((agentId, isTyping) => {
    updateConversation(agentId, { agentIsTyping: isTyping });
  }, [updateConversation]);

  const setConversationID = useCallback((agentId, conversationID) => {
    updateConversation(agentId, { conversationID });
  }, [updateConversation]);

  const setShowStopButton = useCallback((agentId, showStopButton) => {
    updateConversation(agentId, { showStopButton });
  }, [updateConversation]);

  const setIsSendMessageClicked = useCallback((agentId, isSendMessageClicked) => {
    updateConversation(agentId, { isSendMessageClicked });
  }, [updateConversation]);

  const contextValue = {
    conversations,
    communicationConfig,
    getConversation,
    updateConversation,
    clearConversation,
    setMessages,
    setAgentIsTyping,
    setConversationID,
    setShowStopButton,
    setIsSendMessageClicked
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}; 